import { useEffect, useRef, useCallback } from 'react';

/**
 * Hook to detect user inactivity and trigger callbacks before/after timeout
 * @param {Function} onIdle - Callback when user has been idle for full timeout
 * @param {Function} onWarning - Callback when user is approaching timeout (warningMs before)
 * @param {number} timeoutMs - Total idle duration before logout
 * @param {number} warningMs - How many ms before timeout to fire onWarning
 * @param {boolean} enabled - Whether idle detection is active
 */
const useIdleTimeout = (onIdle, onWarning, timeoutMs, warningMs, enabled = true) => {
  const idleTimeoutRef = useRef(null);
  const warningTimeoutRef = useRef(null);
  const lastActivityRef = useRef(Date.now());
  const warningFiredRef = useRef(false);

  const activityEvents = [
    'mousedown',
    'mousemove',
    'keydown',
    'scroll',
    'touchstart',
    'click',
    'wheel',
    'api-activity'
  ];

  const clearTimers = useCallback(() => {
    if (idleTimeoutRef.current) clearTimeout(idleTimeoutRef.current);
    if (warningTimeoutRef.current) clearTimeout(warningTimeoutRef.current);
  }, []);

  const resetTimer = useCallback(() => {
    lastActivityRef.current = Date.now();
    warningFiredRef.current = false;
    clearTimers();

    if (!enabled) return;

    // Schedule warning before idle logout
    const timeUntilWarning = timeoutMs - warningMs;
    if (timeUntilWarning > 0 && onWarning) {
      warningTimeoutRef.current = setTimeout(() => {
        warningFiredRef.current = true;
        onWarning();
        // After warning fires, schedule the actual logout
        idleTimeoutRef.current = setTimeout(() => {
          onIdle();
        }, warningMs);
      }, timeUntilWarning);
    } else {
      // No warning — go straight to logout
      idleTimeoutRef.current = setTimeout(() => {
        onIdle();
      }, timeoutMs);
    }
  }, [onIdle, onWarning, timeoutMs, warningMs, enabled, clearTimers]);

  const handleActivity = useCallback(() => {
    resetTimer();
  }, [resetTimer]);

  useEffect(() => {
    if (!enabled) {
      clearTimers();
      return;
    }

    resetTimer();

    activityEvents.forEach(event => {
      document.addEventListener(event, handleActivity, { passive: true });
    });

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        const elapsed = Date.now() - lastActivityRef.current;
        if (elapsed >= timeoutMs) {
          onIdle();
        } else if (warningFiredRef.current) {
          // Warning modal is already showing but the logout timer was throttled
          // by the browser while the tab was in the background. Re-arm the logout
          // for the actual remaining time without resetting lastActivityRef.
          clearTimers();
          const remaining = timeoutMs - elapsed;
          idleTimeoutRef.current = setTimeout(onIdle, remaining);
        } else {
          resetTimer();
        }
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      clearTimers();
      activityEvents.forEach(event => {
        document.removeEventListener(event, handleActivity);
      });
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [enabled, handleActivity, resetTimer, onIdle, timeoutMs, clearTimers]);

  return {
    resetTimer,
    getLastActivity: () => lastActivityRef.current,
    getRemainingTime: () => Math.max(0, timeoutMs - (Date.now() - lastActivityRef.current)),
  };
};

export default useIdleTimeout;
